import { useEffect, useMemo, useRef, useState } from "react";
import {DiagramManager} from '../../diagram/DiagramManager'
import { useDispatch, useSelector } from "react-redux";
import { getBottomDrawerStatus, getCodeEditStatus, getElementOnFocus, getReRender, getRightDrawerStatus } from "../../../../store/global/selectors";
import { handleSnackBar, invokeGetService, setFocusElement, setForcedFocus, setIsDocumentClicked, setReRender, setSelectedTransactionData, setTabOnFocus, toggleBottomDrawer, toggleRightDrawer } from "../../../../store/global/actions";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import DeleteEventAlert from "../../alerts/DeleteEventAlert";
import DeleteTransactionAlert from "../../alerts/DeleteTransactionAlert";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import Event from "../../../tabs/Event";
import TransactionElements from "../../transactionDrawerContent/TransactionElements";
import Transaction from "../../../tabs/Transaction";
import Delegate from "../../../tabs/Delegate";
import DigitalTwin from "../../../tabs/DigitalTwin";
import Analytics from "../../../tabs/Analytics";
import EventTypes from "../../../tabs/EventTypes";
import MessageSchemaModal from "../../modals/messageSchemaModal";
import { UtilityFunctionDialog } from "../../utility_functions/UtilityFunctionDialog";
import { filterQuickViewMessages } from "../../../../utils/transactionUtils";
import { mediaTypes, typeItems } from "../../../../config/fields";

const typeMapping = {
  "nested_transaction": "Child Transaction",
  "analytics": "Clusterlet",
  "external_function": "Delegate",
  "clusterlet": "Digital Twin"
}

export default function DiagramCanvas({transactionId, setLoading, selectedTransaction, allConnnections, allEvents, allClusterlets, allTransactions, allMessages, utilityFunctions}) {
  const containerRef = useRef();
  const managerRef = useRef(null);
  const transactionGroups = [7, 9, 19];

  const dispatch = useDispatch();
  const history = useHistory();

  const [slug, blueprintID] = window.location.href?.split('/').slice(4);
  const [isBottomDrawer, setOpenDrawer] = useState(false)
  const [drawerElement, setDrawerElement] = useState({})
  const [structuredDrawerElement, setStructuredDrawerElement] = useState({})

  const elementOnFocus = useSelector(getElementOnFocus);
  const reRender = useSelector(getReRender);
  const codeEditStatus = useSelector(getCodeEditStatus);
  

  const [showTransactionDeleteAlert, setShowTransactionDeleteAlert] = useState(false);
  const [showEventDeleteAlert, setShowEventDeleteAlert] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); 
  const [messages, setMessages] = useState(allMessages)
  const [selectedTransactionData, setSelectedTransactionData] = useState(selectedTransaction);
  const [clusterlets, setClusterlets] = useState(allClusterlets);
  const [events, setEvents] = useState(allEvents);
  const [transactions, setTransactions] = useState(allTransactions);
  const [connections, setConnections] = useState(allConnnections);
  const [bottomDrawerTitle, setBottomDrawerTitle] = useState('') 
  const [quickViewMessages, setQuickViewMessages] = useState([])
  const [selectedUtilityFunction, setSelectedUtilityFunction] = useState(null); 

  const checkCodeEditorStatusOnElementClick = () => {
    try {
      if (codeEditStatus) {
          dispatch(setIsDocumentClicked());
      }
      return codeEditStatus;
    } catch (error) {
      console.error(`Handling code editor status on transaction elements click failed. ${error}`);
      return false;
    }
  }

  const handleSelectedItem = () => {
    try {
        const { id, type, name, onsend = false, onrecv = false, parent, uuid, data} = selectedItem;

        if (codeEditStatus) {
            dispatch(setIsDocumentClicked());
            return;
        }

        if(elementOnFocus?.id === id && !onsend && !onrecv) return; 

        const focusElement = {
          transaction: transactionId,
          id,
          type,
          name,
          mode: "edit",
        };

        const tabOnFocus = onsend ? 4 : onrecv ? 5 : 0;

        if (type === "event") {
          const event = events.find((item) => item?._id === id) || data;
          const srcNode = clusterlets.find((c) => c._id === event?.src);
          const dstNode = clusterlets.find((c) => c._id === event?.dst);
          const protocol =
            srcNode?.type === 1
              ? srcNode?.attributes?.type : dstNode?.type === 1
              ? dstNode?.attributes?.type : undefined;

          focusElement.item = event ;
          focusElement.protocol = protocol;
        } else if (type === "transactions" ) {
          const transaction = transactions.find((item) => item?._id === id) || data;
          focusElement.item = transaction;
          
        }else if(type === "nested_transaction"){
          const transaction = transactions.find((item) => item?._id === id) || data;
          focusElement.parent = parent; 
          focusElement.item = transaction;

        } else if(type === "invoked_transaction"){
          const transaction = transactions.find((item) => item?._id === id || data);
          focusElement.uuid = uuid;
          focusElement.parent = parent; 
          focusElement.item = transaction;
        } else if(type === "external_function"){
          const delegate = clusterlets?.find(item => item?._id === id);
          focusElement.protocol = delegate?.attributes?.type;
          focusElement.icon = delegate?.icon;
        } else if(type === "clusterlet" || type === "analytics"){
          const clusterlet = clusterlets?.find(item => item?._id === id);
          focusElement.icon = clusterlet?.icon;
        }
        // console.log({focusElement})

        // setDrawerElement(focusElement)
        dispatch(setFocusElement(focusElement));
        if (onsend || onrecv) dispatch(setForcedFocus(true));
        dispatch(setTabOnFocus(tabOnFocus));

    } catch (error) {
      console.error(`Handling selected item in the diagram failed. ${error}`);
    }
  };


  const destroyDiagramManager = () => {
    managerRef.current?.destroy();
  };

  const clearPaperAndReloadElements = (data, preventConstructsUpdateFlag, preventCompleteRerenderFlag) => {
    if (preventConstructsUpdateFlag !== true) {
      const st_messages = buildMessageStructure({transaction: data?.transaction, allEvents: data?.allEvents, allTransactions: data?.allTransaction, allMessages: data?.messages, allFields: data?.allFields})
      managerRef.current?.updateTransactionData({
        selectedTransaction: data.transaction,
        clusterlets: data.allClusterlets,
        events: data.allEvents,
        transactionClusterlets: data.clusterlets,
        transactionEvents: data.events,
        transactions: data.allTransaction,
        connections: data.connections,
        messages: st_messages
      });
    }


    if(preventCompleteRerenderFlag !== true ){
      managerRef.current?.clearAllElements();
      managerRef.current?.init();
    }

    setLoading(false);
  }

  

  function buildMessageStructure({ transaction, allTransactions, allEvents, allMessages, allFields }) {
    // Helper: find event by ID
    function getEventById(eventId) {
      return allEvents.find(ev => ev._id === eventId);
    }
  
    // Helper: find message by ID
    function getMessageById(messageId) {
      return allMessages.find(msg => msg._id === messageId);
    }
  
    // Build field tree recursively
    function buildFieldTree(messageId) {
      // top-level fields = fields whose parent.id === messageId
      const topFields = allFields.filter(field =>
        field.parent && field.parent.some(p => p.id === messageId && p.type === "message")
      ).map(field => {
        const type = field.attributes?.type;
  
        const readableType =
          typeItems.find(item => item.value === type)?.title ||
          mediaTypes.find(item => item.value === type)?.title ||
          allMessages?.find(item => item._id?.toString() === type)?.name ||
          type;
    
        return {
          ...field,
          readableType
        };
      });
  
      function buildChildren(fieldId) {
        const children = allFields.filter(f =>
          f.parent && f.parent.some(p => p.id === fieldId && p.type === "field")
        ).map(field => {
          const type = field.attributes?.type;
    
          const readableType =
            typeItems.find(item => item.value === type)?.title ||
            mediaTypes.find(item => item.value === type)?.title ||
            allMessages?.find(item => item._id?.toString() === type)?.name ||
            type;
      
          return {
            ...field,
            readableType
          };
        });
        
        return children.map(child => ({
          fieldId: child._id,
          fieldName: child.name,
          attributes: child.attributes,
          type: child.type,
          readableType: child.readableType,
          children: buildChildren(child._id)
        }));
      }
  
      return topFields.map(f => ({
        fieldId: f._id,
        fieldName: f.name,
        attributes: f.attributes,
        type: f.type,
        readableType: f.readableType,
        children: buildChildren(f._id)
      }));
    }
  
    // Build messages from transaction events
    const messages = (transaction.events || []).map(eventRef => {
      const ev = getEventById(eventRef.id);
      if (!ev) return null;
  
      const msg = getMessageById(ev.message);
      if (!msg) return null;
  
      return {
        eventId: ev._id,
        evenName: ev.name,
        messageId: msg._id,
        fieldName: msg.name,
        children: buildFieldTree(msg._id)
      };
    }).filter(Boolean);
  
    // Handle nested transactions (flatten results)
    const nested = (transaction.nestedTransactions || []).flatMap(nestedTx => {
      const txDetails = allTransactions.find(tx => tx._id === nestedTx.id);
      if (!txDetails) return [];
      return buildMessageStructure({ transaction: txDetails, allTransactions, allEvents, allMessages, allFields });
    });
  
    // Return only simplified format
    return [...messages, ...nested];
  }

  const getTransactionAndAssociates = async (createMangerObj=false) => {
    try {
      const preventConstructsUpdateFlag = managerRef?.current?.preventConstructsUpdate;
      const preventCompleteRerenderFlag = managerRef?.current?.preventCompleteRerender;
      dispatch( invokeGetService("getTransactionAssociates", [transactionId]) ).then(response => {
        if (response?.status) {
          const data = response.data;
          
          clearPaperAndReloadElements(data, preventConstructsUpdateFlag, preventCompleteRerenderFlag);

          setSelectedTransactionData(data.transaction);
          setEvents(data?.allEvents);
          setClusterlets(data?.allClusterlets);
          setTransactions(data?.allTransaction);
          setConnections(data?.connections);
          
          const st_messages = buildMessageStructure({transaction: data?.transaction, allEvents: data?.allEvents, allTransactions: data?.allTransaction, allMessages: data?.messages, allFields: data?.allFields})
          setMessages(st_messages)
          setQuickViewMessages(filterQuickViewMessages({quickViewMessages, events: data?.allEvents, messages: st_messages}));
          setLoading(false);
          
        } else if (response?.code === 255 && response.transactionNotFound) {
          console.log("--Transaction not  found--")
          if(elementOnFocus)  dispatch(setFocusElement(null));
          setOpenDrawer(false);
          history.push(`/blueprint/${slug}/${blueprintID}/system-model`);
        } else {
          console.log("--Response not found--")
          if(elementOnFocus)  dispatch(setFocusElement(null));
          setOpenDrawer(false);
          console.error("Failed to fetch transaction details");
        }
        clearLoaders()
      }).catch(error => {
        console.error("Error while fetching transaction details", error);
        setLoading(false);
        if(elementOnFocus)  dispatch(setFocusElement(null));
        setOpenDrawer(false);
      })
      managerRef.current.preventConstructsUpdate = false;
      managerRef.current.preventCompleteRerender = false;
    } catch (error) {
      console.error("Error while fetching transaction details", error);
    } finally {
      
    }
  };

  const onEventDeleteComplete = () => {
    try {
      setShowEventDeleteAlert(false);

    dispatch(handleSnackBar({ open: true, message: 'Event deleted', severity: 'success' }));
    managerRef?.current?.setRenderAndUpdateFlags({preventRerender: true});
    dispatch(setReRender());

        
    } catch (error) {
      console.error(`Handling event deletion in the diagram failed. ${error}`);
    }
  } 


  const onDeleteConfirmation = ({id, type}) => {
    try {
      
    if(elementOnFocus?.id === itemToDelete?.id){
      dispatch(setFocusElement({
          transaction: transactionId,
          id: transactionId,
          name: selectedTransactionData?.name,
          desc: selectedTransactionData?.desc || "",
          type: "transaction",
          action: "edit"
      }));
      dispatch(setTabOnFocus(0));  
    }

    managerRef?.current?.setRenderAndUpdateFlags({preventRerender: true});
    if(type === "event"){
      setQuickViewMessages(quickViewMessages?.filter(item => item?.message?.eventId !== id))
      managerRef?.current?.removeElementsFromDiagram({id, type});
    }else if(type === "nested_transaction"){
      managerRef?.current?.removeElementsFromDiagram({id,  type});
    }else if(type === "invoked_transaction"){
      managerRef?.current?.removeElementsFromDiagram({ id, type});
    }



    } catch (error) {
      console.error(`Handling event delete confirmation in the diagram failed. ${error}`);
    }
  }

  const onTransactionDeleteComplete = (type, action) => {
    try {
      
    if (action) {
      switch (action) {
        case 'delete':
          dispatch(handleSnackBar({ open: true, message: `${type} deleted`, severity: "success" }));
          break;
        case 'unnest':
          break;
        default: 
          break;
      }
    }
    setShowTransactionDeleteAlert(false);
    dispatch(setReRender());


    } catch (error) {
      console.error(`Handling transaction delete confirmation in the diagram failed. ${error}`);
    }
  } 

  const onUpdateComplete = ({id, type, data}) => {
    // managerRef?.current?.handleElementUpdate ({id, type, data});
    // managerRef?.current?.setRenderAndUpdateFlags({preventRerender: true});
  }

  const onCloseEventDelete = () => {
    setShowEventDeleteAlert(false);
    setItemToDelete(null);
  }

  const onCloseTransactionDelete = () => {
    setShowTransactionDeleteAlert(false);
    setItemToDelete(null);
  }

  const onElementRename = (updatedName) => {
    let title = "";
    if (typeMapping[updatedName?.type]) {
      title = `${capitalizeFirstLetter(typeMapping[updatedName?.type])} | ${updatedName?.name}`
    } else if (updatedName?.transaction) {
      title = `${capitalizeFirstLetter(updatedName?.type?.replace(/_/g, ' '))} | ${updatedName?.name}`
    } else {
      title = `${capitalizeFirstLetter(updatedName?.type?.replace(/_/g, ' '))}`
    }

    setBottomDrawerTitle(title);
    if(elementOnFocus != null) dispatch(setFocusElement(updatedName));
    dispatch(setReRender());
  }

  const getRestructuredDrawerElement = (element) => {
    const type = element.type;
    const id = element.id;

    let clusterletsGroups = ['clusterlet', 'external_function', 'analytics', 'sensor', 'user', 'controller_delegate'];
    let eventGroups = ['event'];
    let transactionGroups = ['transaction']

    if (clusterletsGroups.includes(type)) {
        return elementOnFocus;
    }else if (eventGroups.includes(type)) {
        if (elementOnFocus?.templateEvent !== undefined) {
            return { ...elementOnFocus.templateEvent, type: 8 }
        }
        return { id: elementOnFocus.id, item: elementOnFocus.item, name: elementOnFocus.name , type: 8, protocol: elementOnFocus.protocol, action: "edit" }
    } else if (transactionGroups.includes(type)) {
        return { id: id, name: elementOnFocus?.name, type: 7, action: "edit" };
    } else if (type === 'nested_transaction') {
        return { id: id, name: elementOnFocus?.name, type: 9, parent: elementOnFocus?.parent, action: "edit", item: elementOnFocus?.item };
    } else if (type === 'invoked_transaction') {
        return { id: id, name: elementOnFocus?.name, uuid: elementOnFocus?.uuid, parent: elementOnFocus?.parent, type: 19, action: "edit", item: elementOnFocus?.item };
    } 

    return element;
  }

  // To expand the parent of the selected item from the config panel
  const expandParentOnItemSelectionFromConfigPanel = (item) => {
    if(managerRef.current) managerRef.current?.expandParentOfSelectedItem(item);
  }


  const handleDrawerClose = () => {
    setOpenDrawer(false); 
    dispatch(setFocusElement(null));
    managerRef?.current?.removeHighlightingInDiagram();

    managerRef.current?.clearAllElements();
    managerRef.current?.init();

  }

  const onCreateInvokedTransaction = ({status=true, data}) => {
    try {
      
    if(status){
      managerRef?.current?.handleInvokeTransactionCreation(data);
      managerRef?.current?.setRenderAndUpdateFlags({preventRerender: true});
    }else{
      managerRef?.current?.setRenderAndUpdateFlags(false, false);
    }



    } catch (error) {
      console.error(`Handling invoke transaction create in the diagram failed. ${error}`);
    }
  }


  const removeExistingInlineInput = () => {
    try {
      const el = document.getElementById("custom_name_field");
      if (!el) return; // no input to remove
  
      // Safely detach event listeners if they were stored
      if (typeof el.__onBlur === "function") {
        el.removeEventListener("blur", el.__onBlur);
        delete el.__onBlur;
      }
  
      if (typeof el.__onKeydown === "function") {
        el.removeEventListener("keydown", el.__onKeydown);
        delete el.__onKeydown;
      }
  
      // Remove the element from DOM if it still exists
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    } catch (err) {
      console.warn("Failed to clean up inline input:", err);
    }
  };

  // Capitalize the first letter of every word
  function capitalizeFirstLetter(str) {
    try {
      if (!str || typeof str !== "string") return "";

      const formattedStr = str
        .toLowerCase() // optional: normalize case first
        .split(" ")
        .filter(word => word.trim() !== "") // remove extra spaces
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return formattedStr;
    } catch (error) {
      console.error("Error capitalizing string:", error);
      return str;
    }
  }

  const clearLoaders = () => {
    localStorage.removeItem("SHOW_LOADER");
  }

  // Initial rendering of paper and elements in it
  useEffect(() => {
    if(transactionId){
      const diagramManager = new DiagramManager(containerRef.current, {
        dispatch,
        selectedTransaction: selectedTransactionData,
        clusterlets: clusterlets,
        events: events,
        transactionClusterlets: clusterlets,
        transactionEvents: events,
        transactions: transactions,
        connections: connections,
        utilityFunctions: utilityFunctions,
        handleSelectedItem: setSelectedItem,
        handleDeleteItem: setItemToDelete,
        messages,
        setSelectedUtilityFunction,
        setQuickViewMessages,
        onElementRename,
        removeExistingInlineInput,
        checkCodeEditorStatus: checkCodeEditorStatusOnElementClick
      });
      diagramManager.init();

      managerRef.current = diagramManager;
    }

    // To apply style to main-con
    const element = document.getElementById("main-content-div");
    if (element) element.style.setProperty("overflow-y", "hidden", "important");


    return () => {
      destroyDiagramManager();
      removeExistingInlineInput();
      if (element) element.style.removeProperty("overflow-y"); // Reset on unmount
    };
  }, [transactionId]);

  // To re-render the transaction elements in the diagram when an entity is updated
  useEffect(() => {
    if(localStorage.getItem("SHOW_LOADER")){
      setOpenDrawer(false);
      dispatch(setFocusElement(null));
      setLoading(true);
    }
    clearLoaders();

    if(transactionId && selectedTransaction){
        getTransactionAndAssociates(false);
      }
  }, [reRender]);


  // To handle the deletion of item from the diagram
  useEffect(() => {
    if(itemToDelete?.type === "event"){
      setShowEventDeleteAlert(true);
    }else if(itemToDelete?.type === 7 || itemToDelete?.type === 9){
      setShowTransactionDeleteAlert(true);
    }
  }, [itemToDelete])


  // To  handle the selection of items from the diagram
  useMemo(() => {
    if(selectedItem) handleSelectedItem();
  }, [selectedItem])


  // Set the title of the config panel and highlight the elemment on focus in the diagram, when selected item is being changed from the bottom drawer
  useEffect(() => {
    if(elementOnFocus && (elementOnFocus.transaction || elementOnFocus?.screen === "transaction" )){
      let title = "";
      if(typeMapping[elementOnFocus?.type]){
        title = `${capitalizeFirstLetter(typeMapping[elementOnFocus?.type])} | ${elementOnFocus.name}`
      }else if(elementOnFocus?.transaction){
        title = `${capitalizeFirstLetter(elementOnFocus?.type?.replace(/_/g, ' '))} | ${elementOnFocus?.name}`
      }else{
        title = `${capitalizeFirstLetter(elementOnFocus?.type?.replace(/_/g, ' '))}`
      }

      setBottomDrawerTitle(title);
      setOpenDrawer(true);
      setDrawerElement(elementOnFocus);
    }else{
      managerRef?.current?.removeHighlightingInDiagram();
      setOpenDrawer(false);
    }

    managerRef?.current?.hightLighSelectedElementInDiagram({id: elementOnFocus?.uuid ? elementOnFocus?.uuid : elementOnFocus?.id, type: elementOnFocus?.type });
    managerRef?.current?.scrollToElementIfNeeded({id: elementOnFocus?.uuid ? elementOnFocus?.uuid : elementOnFocus?.id, type: elementOnFocus?.type }, true);
  }, [elementOnFocus])



  //  This function is for watching the change in the width of the transaction main container during expanding and collpsing main sidebar, and update the paper width based on that
  useEffect(() => {
    const element = document.getElementById("main-content-div");
    if (!element) return; // Element might not exist yet

    const handleResize = (entries) => {
      for (let entry of entries) {

        if(managerRef?.current && selectedTransaction) managerRef?.current?.updateTransactionData({selectedTransaction});
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    return () => {
      // To close the configuration panel on screen change
      // if(elementOnFocus && elementOnFocus?.transaction === selectedTransaction?._id){
      //   dispatch(setFocusElement({}))
      //   setOpenDrawer(false)
      // }
    }
  },[])
  

  useMemo(() =>{
    if(drawerElement){
      setStructuredDrawerElement(getRestructuredDrawerElement(drawerElement))
    }
  },[drawerElement])


  const getUITourConfiguration = () => {
    try {
      let showTour = "transactionTabs";

      if(structuredDrawerElement?.type === 8 ){
        showTour = "eventTabs"
      }else if(structuredDrawerElement?.type === 'external_function' || structuredDrawerElement?.type === 'controller_delegate'){
        showTour = "delegateTabs"
      }else if(structuredDrawerElement?.type === 'clusterlet'){
        showTour = "digitalTwinTabs";
      }else if(structuredDrawerElement?.type === 'analytics'){
        showTour = "clusterletTabs";
      }else if (structuredDrawerElement?.type === 'import_messages' || structuredDrawerElement?.type === 'event_types'){
        showTour = "eventTypesTabs";
      }

      return showTour; 
    } catch (error) {
      console.error(`Configuring UI tour failed. ${error}`);
      return "transactionTabs"
    }
  }


  return (
  <>
    <BottomDrawer 
      isOpen={isBottomDrawer} 
      onClose={() => handleDrawerClose()}
      title={bottomDrawerTitle} 
      leftPane={
        <TransactionElements 
          onDeleteConfirmation={onDeleteConfirmation}
          expandParentOnItemSelection={expandParentOnItemSelectionFromConfigPanel}
          data={{
            id: drawerElement?.transaction,
            transaction: {...selectedTransactionData} ,
            allEvents: [...events],
            allClusterlets: [...clusterlets],
            allTransactions: [...transactions]
          }} 
        />
      }
      showTour={getUITourConfiguration()}
    >
      {/* Transaction tabs */}
      {
        transactionGroups.includes(structuredDrawerElement?.type) && 
        <Transaction 
          bottomDrawer={true} 
          element={structuredDrawerElement} 
          onCreateInvokedTransaction={onCreateInvokedTransaction}
          key={drawerElement?.id} 
        />
      }
      
      {/* Event tabs */}
      {
        structuredDrawerElement?.type === 8 && 
        <Event 
          bottomDrawer={true} 
          handleUpdateInDiagram={onUpdateComplete}
          element={structuredDrawerElement}  
          key={drawerElement?.id}
        />
      }
     
      {/* clusterlets tabs */}
      {
        (structuredDrawerElement?.type === 'external_function' || structuredDrawerElement?.type === 'controller_delegate') && 
        <Delegate 
          bottomDrawer
          element={structuredDrawerElement} 
          key={drawerElement?.id} 
        />}
      {
        structuredDrawerElement?.type === 'clusterlet' && 
        <DigitalTwin  
          bottomDrawer
          element={structuredDrawerElement} 
          key={drawerElement?.id} 
        />
      }
      {
        structuredDrawerElement?.type === 'analytics' &&
        <Analytics 
          bottomDrawer  
          element={structuredDrawerElement} 
          key={drawerElement?.id} 
        />
      }
      {
        (structuredDrawerElement?.type === 'import_messages' || structuredDrawerElement?.type === 'event_types') &&
        <EventTypes element={structuredDrawerElement} bottomDrawer openedTab={structuredDrawerElement?.type === 'event_types' ? 0 : 1} />
      }
    </BottomDrawer>

    {
      quickViewMessages.filter(item => item?.message).map(item => {
        return (
          <MessageSchemaModal setQuickViewMessages={setQuickViewMessages} key={item?.message?.eventId} {...item} />
        )
      })
    }

    <div 
      ref={containerRef}
      id="transaction_container" 
      className={`${selectedTransaction?.name}_container`}
      style={{
        height: 'calc(100vh - 70px)',
        width: '100%', 
        padding: `0 !important`,
        marginTop: '70px',  
        position: 'relative',  
        overflow: 'auto',
    }}>
      {itemToDelete && showEventDeleteAlert &&  <DeleteEventAlert onCompletion={onEventDeleteComplete} onDeleteConfirmation={onDeleteConfirmation} setShowDeleteEventAlert={setShowEventDeleteAlert} eventId={itemToDelete?.id} onClose={onCloseEventDelete} />}
      {itemToDelete && showTransactionDeleteAlert && <DeleteTransactionAlert onCompletion={onTransactionDeleteComplete} onDeleteConfirmation={onDeleteConfirmation} element={itemToDelete} onClose={onCloseTransactionDelete} /> }
    </div>

    {selectedUtilityFunction && <UtilityFunctionDialog handleClose={() => setSelectedUtilityFunction(null)} setSelectedUtilityFunction={setSelectedUtilityFunction} selectedUtilityFunction={selectedUtilityFunction}/>}

  </>
  );
}
